import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { num } from "../lib/holdingsMath";
import { Modal } from "./ui";
import { Plus, Layers, Receipt, Wallet, ExternalLink, CreditCard, PinOff, AlertTriangle } from "lucide-react";

// 37 base currency codes for every column selector; Runners can add alternates per column.
export const BASE_CODES = [
  "AUD", "BMD", "BSD", "BZD", "CAD", "CHF", "CNY", "EURO", "GBP", "GGP", "GIP", "HKD",
  "INR", "JEP", "JPY", "KGS", "KPW", "KRW", "KYD", "LRD", "MYR", "NAD", "NGN", "NOK",
  "NZD", "OMR", "PHP", "PKR", "QAR", "RUB", "SAR", "SEK", "SGD", "TVD", "TWD", "USD", "ZAR",
];
const COLS = 12;
const BOXES = 10;
// Default account names, read top→down, with a blank divider between the upper and lower sets.
const DEFAULT_LABELS = ["Income", "Operating", "Payroll", "Petty Cash", "Tax", "Benefits", "Refunds", "Future and Strategies", "Contingencies", "Savings"];
const DIVIDER_AFTER = 5; // blank divider rendered before this box index
const defaultCols = () => Array.from({ length: COLS }, (_, i) => ({ code: BASE_CODES[i] || BASE_CODES[0], custom: [] }));

export default function CurrencyMasterDashboard() {
  const [cols, setCols] = useState(defaultCols());
  const [cells, setCells] = useState({}); // `${c}-${b}` -> { id, label, balance }
  const [loaded, setLoaded] = useState(false);
  const cellsRef = React.useRef(cells); cellsRef.current = cells;
  const colsRef = React.useRef(cols); colsRef.current = cols;
  const idRef = React.useRef({});      // key -> currency record id (authoritative across async)
  const chainRef = React.useRef({});   // key -> serialized save promise chain
  const [activity, setActivity] = useState(null); // { pool, items, loading }
  const [unplaced, setUnplaced] = useState([]);   // Currencida accounts not yet slotted into the grid
  const [overview, setOverview] = useState([]);   // live balances + debt classification per account
  const [rates, setRates] = useState({});          // account id -> rate to base (for approx net worth)
  const [showAllCols, setShowAllCols] = useState(false); // false = hide columns with no placed accounts
  const [nwMin, setNwMin] = useState("");   // warn if approx net worth drops below this (base)
  const [utilMax, setUtilMax] = useState(""); // warn if any card utilisation exceeds this %
  const [thrEdit, setThrEdit] = useState(false);
  const [debtRed, setDebtRed] = useState(() => (localStorage.getItem("md_debt_red") ?? "1") !== "0");
  const toggleDebtRed = () => setDebtRed((v) => { const n = !v; localStorage.setItem("md_debt_red", n ? "1" : "0"); return n; });
  const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const KIND_LABEL = {
    equity_buy: "Equity purchase", equity_sell: "Equity sale", equity_dividend: "Dividend",
    equity_fees: "Fees & commissions", lending_outlay: "Lending outlay", lending_income: "Lending income",
    adjust: "Adjustment",
  };
  const openActivity = async (cell) => {
    if (!cell?.id) return;
    setActivity({ pool: cell, items: [], loading: true });
    try {
      const r = await client.get("/transactions");
      const items = (r.data || [])
        .map((x) => ({ id: x.id, ...(x.data || x) }))
        .filter((x) => x.pool_id === cell.id)
        .sort((a, b) => String(b.at || b.created_at || "").localeCompare(String(a.at || a.created_at || "")))
        .slice(0, 40);
      setActivity({ pool: cell, items, loading: false });
    } catch (e) { setActivity({ pool: cell, items: [], loading: false }); }
  };

  const load = async () => {
    try {
      const [opt, cur, ov] = await Promise.all([
        client.get("/options/currency"),
        client.get("/currencies"),
        client.get("/ledger/currency-overview").catch(() => ({ data: { accounts: [] } })),
      ]);
      setOverview(ov.data?.accounts || []);
      const rmap = {};
      (cur.data || []).forEach((r) => { rmap[r.id] = num(r.rate) || 1; });
      setRates(rmap);
      const thr = opt.data?.md_thresholds;
      if (Array.isArray(thr)) { setNwMin(thr[0] == null ? "" : String(thr[0])); setUtilMax(thr[1] == null ? "" : String(thr[1])); }
      const saved = opt.data?.master_dashboard;
      let next = defaultCols();
      if (Array.isArray(saved) && saved.length) {
        next = Array.from({ length: COLS }, (_, i) => ({
          code: saved[i]?.code || BASE_CODES[i] || BASE_CODES[0],
          custom: Array.isArray(saved[i]?.custom) ? saved[i].custom : [],
        }));
      }
      setCols(next);
      const map = {}; const ids = {};
      (cur.data || []).forEach((r) => {
        if (r.md_col != null && r.md_box != null) {
          const key = `${r.md_col}-${r.md_box}`;
          if (map[key]) return; // ignore any stray duplicates
          map[key] = { id: r.id, label: r.name || "", balance: r.balance ?? "" };
          ids[key] = r.id;
        }
      });
      // Seed default names for boxes without a saved record (persisted lazily on first edit).
      for (let c = 0; c < COLS; c++) {
        for (let b = 0; b < BOXES; b++) {
          const key = `${c}-${b}`;
          if (!map[key]) map[key] = { label: DEFAULT_LABELS[b] || "", balance: "" };
        }
      }
      idRef.current = ids;
      setCells(map);
      const up = (cur.data || [])
        .filter((r) => r.md_col == null || r.md_box == null)
        .map((r) => ({ id: r.id, name: r.name || "(unnamed)", balance: r.balance, kind: r.kind || "Deposit", type: r.type || "", credit_limit: r.credit_limit || 0 }));
      setUnplaced(up);
    } catch (e) { /* noop */ } finally { setLoaded(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveCols = async (next) => {
    setCols(next);
    try { await client.put("/options/currency/master_dashboard", { values: next }); } catch (e) { /* noop */ }
  };
  const saveThresholds = async () => {
    try {
      await client.put("/options/currency/md_thresholds", {
        values: [nwMin === "" ? null : num(nwMin), utilMax === "" ? null : num(utilMax)],
      });
      setThrEdit(false);
    } catch (e) { /* noop */ }
  };
  const setColCode = (c, code) => { const next = cols.map((x, i) => i === c ? { ...x, code } : x); saveCols(next); };
  const addCustomCode = (c) => {
    const v = (window.prompt(TL("Add an alternate currency code for this column (e.g. AED, BTC, USDT):")) || "").trim().toUpperCase();
    if (!v) return;
    const next = cols.map((x, i) => i === c ? { ...x, custom: [...new Set([...(x.custom || []), v])], code: v } : x);
    saveCols(next);
  };

  const setCell = (c, b, patch) => setCells((m) => ({ ...m, [`${c}-${b}`]: { ...(m[`${c}-${b}`] || {}), ...patch } }));

  // Serialized upsert per box so a create in flight is never duplicated by a second blur.
  const persistCell = (c, b) => {
    const key = `${c}-${b}`;
    const run = async () => {
      const cell = cellsRef.current[key] || {};
      const label = (cell.label || "").trim();
      const code = colsRef.current[c]?.code || "";
      const id = idRef.current[key];
      if (!label) {
        if (id) { try { await client.delete(`/currencies/${id}`); } catch (e) { /* noop */ } delete idRef.current[key]; setCells((m) => { const n = { ...m }; delete n[key]; return n; }); }
        return;
      }
      // Don't auto-create a pool from an untouched default / blank column — only when it's meaningful.
      if (!id) {
        const hasBalance = cell.balance != null && String(cell.balance).trim() !== "";
        const isDefault = label === (DEFAULT_LABELS[b] || "");
        if (!code || (isDefault && !hasBalance)) return;
      }
      const data = { name: label, code, region: code, balance: num(cell.balance), rate: 1, type: "Fiat", md_col: c, md_box: b, source: "master_dashboard" };
      try {
        if (id) { await client.put(`/currencies/${id}`, { data }); }
        else { const r = await client.post("/currencies", { data }); const nid = r.data?.id || r.data?.data?.id; if (nid) { idRef.current[key] = nid; setCell(c, b, { id: nid }); } }
      } catch (e) { /* noop */ }
    };
    chainRef.current[key] = (chainRef.current[key] || Promise.resolve()).then(run).catch(() => {});
  };

  const colTotal = (c) => Array.from({ length: BOXES }, (_, b) => num(cells[`${c}-${b}`]?.balance)).reduce((a, x) => a + x, 0);

  const placeAccount = async (acc, c) => {
    if (c === "" || c == null) return;
    c = Number(c);
    let target = -1;
    for (let b = 0; b < BOXES; b++) { if (!idRef.current[`${c}-${b}`]) { target = b; break; } }
    if (target < 0) { window.alert(`Column ${colsRef.current[c]?.code || c + 1} is full — every box already holds an account.`); return; }
    try { await client.put(`/currencies/${acc.id}`, { data: { md_col: c, md_box: target } }); await load(); }
    catch (e) { /* noop */ }
  };
  const openRegister = (id) => { const u = `/ledger/currency/${id}`; const w = window.open(u, "_blank", "width=1100,height=800"); if (!w) window.open(u, "_blank"); };
  const unplaceAccount = async (id) => {
    try { await client.put(`/currencies/${id}`, { data: { md_col: null, md_box: null } }); await load(); }
    catch (e) { /* noop */ }
  };

  const optionsFor = (col) => {
    const set = new Set([...BASE_CODES, ...(col.custom || [])]);
    return [...set];
  };

  // Live debt accounts (credit cards / liabilities owing, or deposits drawn below zero),
  // auto-populated from the right. No manual placement — driven entirely by live balances.
  const debtAccounts = (overview || [])
    .filter((r) => r.is_debt)
    .sort((a, b) => (b.debt || 0) - (a.debt || 0));
  const debtTotal = debtAccounts.reduce((a, r) => a + (r.debt || 0), 0);
  // Approximate total net worth in base currency: Σ(live balance × rate-to-base) across all accounts.
  // "Approximate" because rates are the per-account stored rate, not a live FX feed.
  const rateOf = (id) => (rates[id] != null ? rates[id] : 1);
  const assetsBase = (overview || []).reduce((a, r) => a + (r.balance > 0 ? r.balance * rateOf(r.id) : 0), 0);
  const debtBase = (overview || []).reduce((a, r) => a + (r.balance < 0 ? -r.balance * rateOf(r.id) : 0), 0);
  const netWorthBase = assetsBase - debtBase;
  // Dashboard-level warnings (aggregate net worth + per-card utilisation) — distinct from the
  // existing per-pool alert_low/alert_high flasher and the auto top-up thresholds.
  const nwMinNum = nwMin === "" ? null : num(nwMin);
  const utilMaxNum = utilMax === "" ? null : num(utilMax);
  const nwBreached = nwMinNum != null && netWorthBase < nwMinNum;
  const utilBreaches = (overview || []).filter((r) => utilMaxNum != null && r.kind && r.kind !== "Deposit" && (r.utilisation || 0) > utilMaxNum);
  const anyWarn = nwBreached || utilBreaches.length > 0;
  // A column is "used" when at least one of its boxes holds a placed pool.
  const colUsed = (c) => Array.from({ length: BOXES }, (_, b) => cells[`${c}-${b}`]?.id).some(Boolean);
  const anyUsed = cols.some((_, c) => colUsed(c));
  const isColVisible = (c) => showAllCols || !anyUsed || colUsed(c);
  const hiddenCount = cols.filter((_, c) => !isColVisible(c)).length;
  const debtText = debtRed ? "text-[#DA1E28]" : "text-inherit";

  return (
    <div className="rounded-xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="currency-master-dashboard">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Layers size={16} className="text-[var(--primary)]" />
        <h3 className="font-head text-base">{TL("Master Dashboard")}</h3>
        <div className="ms-auto flex items-center gap-1.5">
          <button type="button" onClick={() => setThrEdit((v) => !v)} data-testid="md-toggle-warnings"
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium hover:bg-black/5 dark:hover:bg-white/10 ${anyWarn ? "border-[#DA1E28]/60 text-[#DA1E28]" : "border-black/15 dark:border-white/20"}`}>
            <AlertTriangle size={12} /> {TL("Warnings")}
          </button>
          <button type="button" onClick={() => setShowAllCols((v) => !v)} data-testid="md-toggle-cols"
            className="rounded-full border border-black/15 px-2.5 py-1 text-[11px] font-medium hover:bg-black/5 dark:border-white/20 dark:hover:bg-white/10">
            {showAllCols ? TL("Hide empty columns") : `${TL("Show all columns")}${hiddenCount ? ` (+${hiddenCount})` : ""}`}
          </button>
          <button type="button" onClick={toggleDebtRed} data-testid="md-toggle-debt-color"
            className="inline-flex items-center gap-1 rounded-full border border-black/15 px-2.5 py-1 text-[11px] font-medium hover:bg-black/5 dark:border-white/20 dark:hover:bg-white/10">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${debtRed ? "bg-[#DA1E28]" : "bg-current"}`} />
            {TL("Debt text:")} {debtRed ? TL("Red") : TL("Black")}
          </button>
        </div>
      </div>
      <p className="mb-3 text-xs opacity-60">
        {TL("12 currency columns × 10 account boxes (upper set / divider / lower set). Name any box to register it as a live currency pool — it becomes targetable by SEBAA (pay), BoxAlong (top-up) and Cluster Control, and by transfers within this app.")}
      </p>
      {loaded && anyWarn && (
        <div className="alert-flash mb-3 rounded-lg border border-[#DA1E28]/50 bg-[#DA1E28]/10 p-3" data-testid="md-warning-banner" role="alert">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#DA1E28]">
            <AlertTriangle size={16} className="shrink-0" />
            <span>{TL("Master Dashboard warnings")} — {(nwBreached ? 1 : 0) + utilBreaches.length} {TL("threshold(s) breached")}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {nwBreached && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2.5 py-1 text-xs dark:bg-black/30" data-testid="md-warn-networth">
                <strong>{TL("Net worth")}</strong><span className="opacity-70">{TL("below")} {money(nwMinNum)}</span>
                <span className="opacity-50">({TL("now")} {money(netWorthBase)})</span>
              </span>
            )}
            {utilBreaches.map((r) => (
              <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2.5 py-1 text-xs dark:bg-black/30" data-testid={`md-warn-util-${r.id}`}>
                <strong>{r.name}</strong><span className="opacity-70">{TL("utilisation")} {r.utilisation}% &gt; {utilMaxNum}%</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {loaded && thrEdit && (
        <div className="mb-3 rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid="md-warning-settings">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--primary)]">
            <AlertTriangle size={13} /> {TL("Warning thresholds")}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs">
              <div className="mb-0.5 opacity-70">{TL("Warn if net worth below (base)")}</div>
              <input type="number" step="any" value={nwMin} onChange={(e) => setNwMin(e.target.value)} placeholder={TL("e.g. 100000")}
                data-testid="md-thr-networth" className="w-44 rounded border border-black/15 bg-white/70 px-2 py-1 text-sm dark:border-white/15 dark:bg-black/30" />
            </label>
            <label className="text-xs">
              <div className="mb-0.5 opacity-70">{TL("Warn if card utilisation above (%)")}</div>
              <input type="number" step="any" value={utilMax} onChange={(e) => setUtilMax(e.target.value)} placeholder={TL("e.g. 80")}
                data-testid="md-thr-util" className="w-40 rounded border border-black/15 bg-white/70 px-2 py-1 text-sm dark:border-white/15 dark:bg-black/30" />
            </label>
            <button type="button" onClick={saveThresholds} data-testid="md-thr-save"
              className="rounded bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-white">{TL("Save thresholds")}</button>
            <span className="text-[11px] opacity-55">{TL("Leave a field blank to disable that warning. These are dashboard-level; per-pool low/high alerts & auto top-ups stay in each account.")}</span>
          </div>
        </div>
      )}
      {loaded && (overview || []).length > 0 && (
        <div className="mb-3 flex flex-wrap items-stretch gap-2" data-testid="md-networth">
          <div className="flex-1 min-w-[220px] rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/[0.06] px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--primary)]">
              <Layers size={12} /> {TL("Approximate total net worth")}
              <span className="ms-auto font-normal normal-case opacity-55">{TL("base currency · est.")}</span>
            </div>
            <div className={`mt-0.5 text-xl font-bold tabular-nums ${netWorthBase < 0 ? "text-[#DA1E28]" : ""}`} data-testid="md-networth-value">
              {netWorthBase < 0 ? "−" : ""}{money(Math.abs(netWorthBase))}
            </div>
          </div>
          <div className="min-w-[150px] rounded-lg border border-black/10 px-3 py-2 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wide opacity-55">{TL("Assets")}</div>
            <div className="mt-0.5 text-base font-semibold tabular-nums text-[#0E9F6E]" data-testid="md-networth-assets">{money(assetsBase)}</div>
          </div>
          <div className="min-w-[150px] rounded-lg border border-black/10 px-3 py-2 dark:border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wide opacity-55">{TL("Debt")}</div>
            <div className={`mt-0.5 text-base font-semibold tabular-nums ${debtBase > 0 ? "text-[#DA1E28]" : "opacity-50"}`} data-testid="md-networth-debt">
              {debtBase > 0 ? `−${money(debtBase)}` : money(0)}
            </div>
          </div>
        </div>
      )}
      {!loaded ? (
        <div className="py-6 text-center text-sm opacity-60" data-testid="md-loading">{TL("Loading…")}</div>
      ) : (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3" style={{ minWidth: COLS * 172 }}>
            {cols.map((col, c) => isColVisible(c) && (
              <div key={c} className="w-[168px] flex-none rounded-lg border border-black/10 dark:border-white/10" data-testid={`md-col-${c}`}>
                <div className="flex items-center gap-1 border-b border-black/10 bg-[var(--primary)]/10 p-1.5 dark:border-white/10">
                  <select value={col.code} onChange={(e) => setColCode(c, e.target.value)} data-testid={`md-currency-${c}`}
                    className="w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-xs font-semibold dark:border-white/15 dark:bg-black/30">
                    <option value="">—</option>
                    {optionsFor(col).map((code) => <option key={code} value={code}>{code}</option>)}
                  </select>
                  <button type="button" onClick={() => addCustomCode(c)} title={TL("Add an alternate currency to this column")}
                    data-testid={`md-add-currency-${c}`} className="flex-none rounded p-1 text-[var(--primary)] hover:bg-[var(--primary)]/10"><Plus size={14} /></button>
                </div>
                <div className="space-y-1.5 p-1.5">
                  {Array.from({ length: BOXES }, (_, b) => {
                    const key = `${c}-${b}`; const cell = cells[key] || {};
                    return (
                      <React.Fragment key={b}>
                        {b === DIVIDER_AFTER && <div className="my-1 border-t border-dashed border-black/25 dark:border-white/25" data-testid={`md-divider-${c}`} />}
                        <div className="rounded border border-black/10 p-1.5 dark:border-white/10" data-testid={`md-box-${c}-${b}`}>
                          <div className="mb-1 flex items-center gap-1">
                            <input value={cell.label || ""} maxLength={40} placeholder={`${TL("Account")} ${b + 1} ${TL("name…")}`}
                              onChange={(e) => setCell(c, b, { label: e.target.value })} onBlur={() => persistCell(c, b)}
                              data-testid={`md-box-label-${c}-${b}`}
                              className="w-full rounded border-0 bg-transparent px-1 py-0.5 text-[11px] font-medium placeholder:opacity-40 focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
                            {cell.id && (
                              <button type="button" onClick={() => openActivity(cell)} title={TL("View recent activity")}
                                data-testid={`md-box-activity-${c}-${b}`}
                                className="flex-none rounded p-0.5 text-[var(--primary)] opacity-60 hover:bg-[var(--primary)]/10 hover:opacity-100"><Receipt size={12} /></button>
                            )}
                            {cell.id && (
                              <button type="button" onClick={() => unplaceAccount(cell.id)} title={TL("Unpeg — send this account back to the unplaced list")}
                                data-testid={`md-box-unplace-${c}-${b}`}
                                className="flex-none rounded p-0.5 opacity-50 hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"><PinOff size={12} /></button>
                            )}
                          </div>
                          <input type="number" value={cell.balance ?? ""} placeholder="0.00"
                            onChange={(e) => setCell(c, b, { balance: e.target.value })} onBlur={() => persistCell(c, b)}
                            data-testid={`md-box-balance-${c}-${b}`}
                            className="w-full rounded border border-black/10 bg-white/60 px-1.5 py-1 text-xs tabular-nums dark:border-white/10 dark:bg-black/30 focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between border-t border-black/10 px-2 py-1 text-[11px] dark:border-white/10" data-testid={`md-col-total-${c}`}>
                  <span className="opacity-60">{TL("Total")}</span>
                  <b className="tabular-nums">{col.code} {colTotal(c).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b>
                </div>
              </div>
            ))}
            {debtAccounts.length > 0 && (
              <div className="sticky right-0 z-20 w-[190px] flex-none self-start rounded-lg border-2 border-[#DA1E28]/45 bg-white shadow-[-10px_0_18px_-8px_rgba(0,0,0,0.25)] dark:bg-[#0b1020]"
                data-testid="md-debt-lane">
                <div className="flex items-center gap-1.5 rounded-t-md border-b border-[#DA1E28]/30 bg-[#DA1E28]/10 px-2 py-1.5">
                  <CreditCard size={14} className={debtText} />
                  <span className={`text-xs font-bold ${debtText}`}>{TL("Debt & Facilities")}</span>
                </div>
                <div className="space-y-1.5 p-1.5">
                  {debtAccounts.map((r) => {
                    const od = r.kind === "Deposit"; // overdrawn deposit vs credit facility
                    return (
                      <div key={r.id} className="rounded border border-[#DA1E28]/25 bg-[#DA1E28]/[0.04] p-1.5" data-testid={`md-debt-${r.id}`}>
                        <div className="flex items-center gap-1">
                          <span className={`min-w-0 flex-1 truncate text-[11px] font-semibold ${debtText}`} title={r.name}>{r.name}</span>
                          <button type="button" onClick={() => openRegister(r.id)} title={TL("Open register")}
                            data-testid={`md-debt-open-${r.id}`}
                            className="flex-none rounded p-0.5 opacity-60 hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"><ExternalLink size={12} /></button>
                        </div>
                        <div className="mt-0.5 flex items-baseline justify-between">
                          <span className="text-[9px] uppercase tracking-wide opacity-55">{od ? TL("Overdrawn") : TL(r.kind)}</span>
                          <b className={`text-xs tabular-nums ${debtText}`}>−{money(r.debt)}</b>
                        </div>
                        {!od && r.credit_limit > 0 && (
                          <div className="mt-0.5 text-[9px] opacity-60">
                            {TL("Avail")} {money(r.available)} · {r.utilisation}% {TL("used")}
                            {r.min_payment ? ` · ${TL("min")} ${money(r.min_payment)}` : ""}
                            {r.payment_due ? ` · ${TL("due")} ${String(r.payment_due).slice(0, 10)}` : ""}
                          </div>
                        )}
                        {od && r.overdraft_limit > 0 && (
                          <div className="mt-0.5 text-[9px] opacity-60">{TL("Overdraft limit")} {money(r.overdraft_limit)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between border-t border-[#DA1E28]/30 px-2 py-1 text-[11px]">
                  <span className="opacity-60">{TL("Total owed")}</span>
                  <b className={`tabular-nums ${debtText}`} data-testid="md-debt-total">−{money(debtTotal)}</b>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {loaded && unplaced.length > 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-[var(--primary)]/40 bg-[var(--primary)]/[0.04] p-3" data-testid="md-unplaced">
          <div className="mb-2 flex items-center gap-2">
            <Wallet size={15} className="text-[var(--primary)]" />
            <span className="text-sm font-semibold">{TL("Unplaced Currencida accounts")}</span>
            <span className="ms-auto text-xs opacity-55">{unplaced.length} {TL("not yet on the grid · created in the account list")}</span>
          </div>
          <p className="mb-2 text-xs opacity-60">{TL("These accounts exist in Currencida but haven’t been slotted into a column above. Pick a column to place one into its next free box, or open its register.")}</p>
          <div className="space-y-1.5">
            {unplaced.map((a) => {
              const isCredit = a.kind && a.kind !== "Deposit";
              return (
                <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-black/10 bg-white/60 px-2.5 py-1.5 text-sm dark:border-white/10 dark:bg-black/20" data-testid={`md-unplaced-${a.id}`}>
                  <span className="flex items-center gap-1.5 font-medium">
                    {isCredit ? <CreditCard size={14} className="text-[#C99415]" /> : <Wallet size={14} className="opacity-45" />}
                    {a.name}
                  </span>
                  {isCredit && <span className="rounded-full bg-[#C99415]/15 px-2 py-0.5 text-[10px] font-medium text-[#8a6410] dark:text-[#e8c774]">{a.kind}</span>}
                  {a.type && <span className="text-xs opacity-45">{a.type}</span>}
                  <span className="text-xs opacity-60 tabular-nums">{TL("Bal")} {num(a.balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  <span className="ms-auto flex items-center gap-1.5">
                    <select defaultValue="" onChange={(e) => { placeAccount(a, e.target.value); e.target.value = ""; }} data-testid={`md-place-${a.id}`}
                      className="rounded border border-black/15 bg-white/70 px-1.5 py-1 text-xs dark:border-white/15 dark:bg-black/30">
                      <option value="">{TL("Place in column…")}</option>
                      {cols.map((col, ci) => <option key={ci} value={ci}>{col.code || `Col ${ci + 1}`}</option>)}
                    </select>
                    <button type="button" onClick={() => openRegister(a.id)} title={TL("Open register")} data-testid={`md-open-${a.id}`}
                      className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-xs dark:border-white/20"><ExternalLink size={12} /> {TL("Open")}</button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {activity && (
        <Modal open={!!activity} onClose={() => setActivity(null)} title={`Recent activity — ${activity.pool?.label || "account"}`} testid="md-activity-modal">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="opacity-60">{TL("Latest money movements into / out of this Currencida account.")}</span>
              <b className="tabular-nums" data-testid="md-activity-balance">Balance: {activity.pool?.balance ?? "—"}</b>
            </div>
            {activity.loading ? (
              <div className="py-6 text-center text-sm opacity-60" data-testid="md-activity-loading">{TL("Loading…")}</div>
            ) : activity.items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-black/15 py-6 text-center text-xs opacity-60 dark:border-white/15" data-testid="md-activity-empty">
                No activity recorded against this account yet. Equity trades, dividends, fees and lending movements will appear here.
              </div>
            ) : (
              <div className="max-h-[50vh] space-y-1 overflow-y-auto pe-1" data-testid="md-activity-list">
                {activity.items.map((x, i) => {
                  const credit = x.direction === "credit";
                  const label = KIND_LABEL[x.kind] || x.kind || "Movement";
                  const tkr = x.meta?.ticker ? ` · ${x.meta.ticker}` : "";
                  return (
                    <div key={x.id || i} className="flex items-center justify-between gap-3 rounded border border-black/10 bg-white/50 px-2.5 py-1.5 text-xs dark:border-white/10 dark:bg-black/20" data-testid={`md-activity-row-${i}`}>
                      <div className="min-w-0">
                        <div className="truncate font-medium">{label}{tkr}{x.scheduled ? " · auto" : ""}</div>
                        <div className="text-[10px] opacity-55">{String(x.at || x.created_at || "").slice(0, 10)}</div>
                      </div>
                      <b className={`tabular-nums ${credit ? "text-green-700 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                        {credit ? "+" : "−"}{Number(x.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </b>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
